import React from 'react'
import GraphiQL from 'graphiql'
import GraphiQLExplorer from "graphiql-explorer";
import { buildClientSchema, getIntrospectionQuery, parse } from "graphql";
import {CleverToolbarButton} from './CleverToolbarButton'

class CleverGraphiQL extends React.Component {
  constructor(props) {
    super(props);

    this.gqlRef = null;
    this.oidcUserManager = null;

    let default_Query = `# Welcome to GraphiQL
#
# GraphiQL is an in-browser tool for writing, validating, and
# testing GraphQL queries.
#
# Type queries into this side of the screen, and you will see intelligent
# typeaheads aware of the current GraphQL type schema and live syntax and
# validation errors highlighted within the text.
#
# GraphQL queries typically start with a "{" character. Lines that starts
# with a # are ignored.
#
# An example GraphQL query might look like:
#
#     {
#       field(arg: "value") {
#         subField
#       }
#     }
#
# Keyboard shortcuts:
#
#  Prettify Query:  Shift-Ctrl-P (or press the prettify button above)
#
#     Merge Query:  Shift-Ctrl-M (or press the merge button above)
#
#       Run Query:  Ctrl-Enter (or press the play button above)
#
#   Auto Complete:  Ctrl-Space (or just start typing)
#
#         Jump to:  Shift-Alt-LeftClick (jump to it in the explorer)
#
#
# Explorer: 
#
# Open the schema explorer using the "Explorer" button above.
# 
# Click on a field to add it.
# Alt-Click on a field to add it and all direct child fields.
#`

    this.state = {
      schema: null,
      query: default_Query,
      explorerIsOpen: false,
      user: null
    };
  }

  graphQLFetcher = (graphQLParams, headers) => {
    if (headers == null) {
      headers = new Headers();
      headers.append('Content-Type', 'application/json');
    }

    if (this.state.user !== null) {
      headers.append('Authorization', 'Bearer ' + this.state.user.access_token)
    }

    if (graphQLParams.operationName) {
      const operation = this.gqlRef.state.operations.find(operation => operation.name.value === graphQLParams.operationName)
      graphQLParams.query = graphQLParams.query.substring(operation.loc.start, operation.loc.end)
    }

    return fetch(window.location.origin + '/graphql', {
      method: 'post',
      headers: headers,
      body: JSON.stringify(graphQLParams),
    }).then(response => response.json());
  }

  componentDidMount() {
    this.graphQLFetcher({
      query: getIntrospectionQuery()
    }).then(result => {
      const editor = this.gqlRef.getQueryEditor();
      editor.setOption("extraKeys", {
        ...(editor.options.extraKeys || {}),
        "Shift-Alt-LeftClick": this.handleInspectOperation
      });

      this.setState({ schema: buildClientSchema(result.data) });
    });

    let Oidc = require('oidc-client');
    fetch(window.location.origin + "/Scripts/static/js/oidcConfig.json").then(response => {
      return response.json();
    }).then(data => {
      if (data != null) {
        if (data.redirect_uri != "") {
          data.redirect_uri = window.location.origin + data.redirect_uri;
        }

        if (data.post_logout_redirect_uri != "") {
          data.post_logout_redirect_uri = window.location.origin + data.post_logout_redirect_uri;
        }
      }

      this.oidcUserManager = new Oidc.UserManager(data);

      let this2 = this;
      this.oidcUserManager.getUser().then(function (user) {
        if (user) {
          console.log("User logged in", user.profile);
          this2.setState({ user })
        }
        else {
          console.log("User not logged in");
        }
      });

      this.oidcUserManager.events.addUserLoaded((user) => { // <- user object passed into the callback
        // do some internal logic with the new user object
        this.setState({ user })
        console.log("addUserLoaded callback called");
      });
    });

    if (this.gqlRef) {
      let query = this.gqlRef._storage.storage.getItem('graphiql:query')
      if (query) {
        this.setState({ query })
      }

      let explorerIsOpen = this.gqlRef._storage.storage.getItem('graphiql:explorerIsOpen')
      if (explorerIsOpen) {
        this.setState({ explorerIsOpen: explorerIsOpen === 'true' })
      }
    }

    window.onbeforeunload = () => {
      this.gqlRef._storage.storage.setItem('graphiql:explorerIsOpen', this.state.explorerIsOpen);
      this.gqlRef._storage.storage.setItem('graphiql:query', this.state.query)
    }
  }

  handleLoginButton = () => {
    if (this.oidcUserManager != null && this.state.user == null) {
      this.oidcUserManager.signinRedirect();
    }
    if (this.state.user != null) {
      this.oidcUserManager.signoutRedirect()
        .catch(function (error) {
          console.error('error while signing out user', error);
        });
    }
  };

  handleInspectOperation = (
    cm,
    mousePos
  ) => {
    const parsedQuery = parse(this.state.query || "");

    if (!parsedQuery) {
      console.error("Couldn't parse query document");
      return null;
    }

    var token = cm.getTokenAt(mousePos);
    var start = { line: mousePos.line, ch: token.start };
    var end = { line: mousePos.line, ch: token.end };
    var relevantMousePos = {
      start: cm.indexFromPos(start),
      end: cm.indexFromPos(end)
    };

    var position = relevantMousePos;

    var def = parsedQuery.definitions.find(definition => {
      if (!definition.loc) {
        console.log("Missing location information for definition");
        return false;
      }

      const { start, end } = definition.loc;
      return start <= position.start && end >= position.end;
    });

    if (!def) {
      console.error(
        "Unable to find definition corresponding to mouse position"
      );
      return null;
    }

    var operationKind =
      def.kind === "OperationDefinition"
        ? def.operation
        : def.kind === "FragmentDefinition"
          ? "fragment"
          : "unknown";

    var operationName =
      def.kind === "OperationDefinition" && !!def.name
        ? def.name.value
        : def.kind === "FragmentDefinition" && !!def.name
          ? def.name.value
          : "unknown";

    var selector = `.graphiql-explorer-root #${operationKind}-${operationName}`;

    var el = document.querySelector(selector);
    el && el.scrollIntoView();
  };

  handleEditQuery = (query) => {
    this.setState({ query });
  }

  handleToggleExplorer = () => {
    this.setState({ explorerIsOpen: !this.state.explorerIsOpen });
  };

  render() {
    return (
      <div className="graphiql-container">
        <GraphiQLExplorer
          schema={this.state.schema}
          query={this.state.query}
          onEdit={this.handleEditQuery}
          onRunOperation={operationName =>
            this.gqlRef.handleRunQuery(operationName)
          }
          explorerIsOpen={this.state.explorerIsOpen}
          onToggleExplorer={this.handleToggleExplorer}
        />
        <GraphiQL
          editorTheme="default"
          fetcher={this.graphQLFetcher}
          ref={gql => { this.gqlRef = gql }}
          schema={this.state.schema}
          query={this.state.query}
          onEditQuery={this.handleEditQuery}
        >
          <GraphiQL.Toolbar>
            <GraphiQL.ToolbarButton
              onClick={() => this.gqlRef.handlePrettifyQuery()}
              title="Prettify Query (Shift-Ctrl-P)"
              label="Prettify"
            />
            <GraphiQL.ToolbarButton
              onClick={() => this.gqlRef.handleToggleHistory()}
              title="Show History"
              label="History"
            />
            <GraphiQL.ToolbarButton
              onClick={this.handleToggleExplorer}
              label="Explorer"
              title="Toggle Explorer"
            />
            <CleverToolbarButton
              onClick={this.handleLoginButton}
              label={this.state.user == null ? "Login" : "Logout"}
              title={
                this.state.user == null 
                ? "Log in with your cleverbridge credentials." 
                : "Logout"
              }
              additionalClasses={
                this.state.user == null 
                ? "loginButton"
                : "logoutButton"
              }
            />
          </GraphiQL.Toolbar>
        </GraphiQL>
      </div>
    )
  }
}

export default CleverGraphiQL;
